import { useCallback, useEffect, useRef, useState } from 'react';
export const canUseIntersectionObserver = () => 'IntersectionObserver' in window &&
    'IntersectionObserverEntry' in window &&
    'intersectionRatio' in window.IntersectionObserverEntry.prototype;
/**
 * This hook currently only supports observing 1 node and will only register the first node given
 * to the "setTargetElement" function, subsequent calls will be ignored.
 */
export function useIntersection({ root, rootMargin, threshold, triggerOnce, }) {
    const [isIntersecting, setIsIntersecting] = useState(false);
    const intersectionObserver = useRef(null);
    const entryRef = useRef(null);
    const setTargetElement = useCallback((node, overrideRoot) => {
        if (node) {
            if (!intersectionObserver.current && canUseIntersectionObserver()) {
                intersectionObserver.current = new IntersectionObserver(([entry], observer) => {
                    if (entry) {
                        setIsIntersecting(entry.isIntersecting);
                        if (triggerOnce && entry.isIntersecting) {
                            observer.unobserve(entry.target);
                            entryRef.current = null;
                        }
                    }
                }, {
                    root: overrideRoot || root,
                    rootMargin,
                    threshold,
                });
            }
            if (node === entryRef.current) {
                // Do not observe an already observed node
                return;
            }
            if (entryRef.current) {
                intersectionObserver.current?.unobserve(entryRef.current);
                setIsIntersecting(false);
            }
            intersectionObserver.current?.observe(node);
            entryRef.current = node;
        }
    }, [root, rootMargin, setIsIntersecting, threshold, triggerOnce]);
    useEffect(() => {
        if (!canUseIntersectionObserver())
            return undefined; // included in react-base-fragment
        return () => intersectionObserver.current?.disconnect();
    }, []);
    return [isIntersecting, setTargetElement];
}
